import React, { FunctionComponent, ReactNode, useCallback, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button, Chip, IconButton, Search, ThemeProvider, Typography } from "@hxnova/react-components";
import { Icon } from "@hxnova/icons";
import { NovaTheme } from "@hxnova/themes";

import "../../../css/filter_bar/trade_filter_panel.css";
import DropdownBelow from "./dropdown_below";
import TradeTreeRows from "../shared/trade_tree_rows";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import getSelectedTradeCodes from "../../services/getters/filter_getters/get_selected_trade_codes";
import getSelectedUniformatTradeCodes from "../../services/getters/filter_getters/get_selected_uniformat_trade_codes";
import tradeHidden, { TradeHiddenEvent } from "../../events/selection/trades/trade_hidden";
import tradeHiddenAll, { TradeHiddenAllEvent } from "../../events/selection/trades/trade_hidden_all";
import tradeShown, { TradeShownEvent } from "../../events/selection/trades/trade_shown";
import tradeShownAll, { TradeShownAllEvent } from "../../events/selection/trades/trade_shown_all";
import useTrades from "../../queries/trades/use_trades";
import { tradeFilterBreakdown } from "../../services/utilities/trade_filter_breakdown";
import { filterTradeNodesByQuery, leafCodesByNode, tradeGroupCodes } from "../../services/utilities/trade_tree";

import type { BreakdownNode } from "../../services/utilities/breakdown";
import type { Dispatch } from "type_aliases";

export type Props = {
  rootNodes: BreakdownNode[]
  selectedCodes: Set<string>
  onToggleTrade: (code: string, on: boolean) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  onClose: () => void
};

const selectedTradesLabel = (count: number): string => `${count} ${count === 1 ? "trade" : "trades"} selected`;

export const TradeFilterPanel: FunctionComponent<Props> = function TradeFilterPanel(props) {
  const { rootNodes, selectedCodes, onToggleTrade, onSelectAll, onDeselectAll, onClose } = props;

  const [query, setQuery] = useState("");
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());

  const leavesByCode = useMemo(() => leafCodesByNode(rootNodes), [rootNodes]);
  const groupCodes = useMemo(() => tradeGroupCodes(rootNodes), [rootNodes]);

  const isSearching = query.trim().length > 0;

  const visibleNodes = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return trimmed ? filterTradeNodesByQuery(rootNodes, trimmed) : rootNodes;
  }, [rootNodes, query]);

  const toggleExpand = useCallback((code: string) => {
    setExpandedCodes(current => {
      const next = new Set(current);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setExpandedCodes(new Set(groupCodes)), [groupCodes]);
  const collapseAll = useCallback(() => setExpandedCodes(new Set()), []);

  const leafCodes = useMemo(() => rootNodes.flatMap(node => leavesByCode[node.id as string] ?? []), [rootNodes, leavesByCode]);
  const selectedCount = useMemo(() => leafCodes.filter(code => selectedCodes.has(code)).length, [leafCodes, selectedCodes]);

  return <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
    <div className="TradeFilterPanel">
      <div className="TradeFilterPanel-header">
        <Typography variant="titleSmall" className="TradeFilterPanel-heading">Trades</Typography>
        <IconButton
          className="TradeFilterPanel-close"
          variant="standard"
          aria-label="Close trades panel"
          onClick={onClose}
        >
          <Icon family="material" name="close" size={20}/>
        </IconButton>
      </div>
      <div className="TradeFilterPanel-search">
        <Search
          className="TradeFilterPanel-searchInput"
          placeholder="Search trades or codes"
          startDecorator={<Icon family="material" name="search" size={20}/>}
          endDecorator={isSearching ? <IconButton
            className="TradeFilterPanel-clearSearch"
            variant="standard"
            aria-label="Clear trade search"
            onClick={() => setQuery("")}
          >
            <Icon family="material" name="close" size={20}/>
          </IconButton> : null}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>
      <div className="TradeFilterPanel-actions">
        <Button
          variant="text"
          className="TradeFilterPanel-action TradeFilterPanel-expandAll"
          onClick={expandAll}
          disabled={isSearching || groupCodes.length === 0 || expandedCodes.size === groupCodes.length}
        >Expand All</Button>
        <span className="TradeFilterPanel-actionSeparator">|</span>
        <Button
          variant="text"
          className="TradeFilterPanel-action TradeFilterPanel-collapseAll"
          onClick={collapseAll}
          disabled={isSearching || groupCodes.length === 0 || expandedCodes.size === 0}
        >Collapse All</Button>
        <span className="TradeFilterPanel-actionSeparator">|</span>
        <Button
          variant="text"
          className="TradeFilterPanel-action TradeFilterPanel-selectAll"
          onClick={onSelectAll}
          disabled={isSearching || leafCodes.length === 0 || selectedCount === leafCodes.length}
        >Select All</Button>
        <span className="TradeFilterPanel-actionSeparator">|</span>
        <Button
          variant="text"
          className="TradeFilterPanel-action TradeFilterPanel-deselectAll"
          onClick={onDeselectAll}
          disabled={isSearching || selectedCount === 0}
        >Deselect All</Button>
      </div>
      <div className="TradeFilterPanel-list">
        {visibleNodes.length === 0 ? (
          <div className="TradeFilterPanel-empty">
            <Typography variant="bodySmall" className="TradeFilterPanel-emptyText">
              {rootNodes.length === 0 ? "No trades found for this project." : "No results found. Try a different name or code"}
            </Typography>
          </div>
        ) : <TradeTreeRows
          nodes={visibleNodes}
          leavesByCode={leavesByCode}
          selectedCodes={selectedCodes}
          expandedCodes={expandedCodes}
          isSearching={isSearching}
          onToggle={onToggleTrade}
          onToggleExpand={toggleExpand}
        />}
      </div>
      <div className="TradeFilterPanel-footer">
        <Chip className="TradeFilterPanel-countTag" label={selectedTradesLabel(selectedCount)}/>
      </div>
    </div>
  </ThemeProvider>;
};

export type ConnectedProps = {
                             disabled?: boolean
                             tradesTsvFilter: boolean
                             icon?: ReactNode
                           } & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

// Owns its open state: the filter bar's menubar click handler closes every menu it owns.
const ConnectedTradeFilterPanel: FunctionComponent<ConnectedProps> = function ConnectedTradeFilterPanel(props) {
  const {
    disabled, projectId, tradesTsvFilter, selectedTradeCodes, selectedUniformatTradeCodes,
    tradeShown, tradeHidden, tradeSelectedAll, tradeDeselectedAll, icon
  } = props;

  const [open, setOpen] = useState(false);
  const openPanel = useCallback(() => setOpen(true), []);
  const closePanel = useCallback(() => setOpen(false), []);

  const { data: trades } = useTrades(projectId);
  const rootNodes = useMemo<BreakdownNode[]>(
    () => Object.values(tradeFilterBreakdown(trades, tradesTsvFilter).breakdown),
    [trades, tradesTsvFilter]);
  const selectedCodes = tradesTsvFilter ? selectedTradeCodes : selectedUniformatTradeCodes;

  // The filter store cascades to the children, so a row reports only its own code.
  const onToggleTrade = useCallback((code: string, on: boolean) => {
    if (on) {
      tradeShown(code);
    } else {
      tradeHidden(code);
    }
  }, [tradeShown, tradeHidden]);

  return <DropdownBelow
    persistent
    open={open}
    openFlyout={openPanel}
    closeFlyout={closePanel}
    disabled={disabled}
    buttonContent="Trades"
    icon={icon}
  >
    <TradeFilterPanel
      rootNodes={rootNodes}
      selectedCodes={selectedCodes}
      onToggleTrade={onToggleTrade}
      onSelectAll={tradeSelectedAll}
      onDeselectAll={tradeDeselectedAll}
      onClose={closePanel}
    />
  </DropdownBelow>;
};

const mapStateToProps = (state, props) => ({
  selectedUniformatTradeCodes: getSelectedUniformatTradeCodes(state, props),
  selectedTradeCodes: getSelectedTradeCodes(state, props),
  projectId: getProjectId(state, props)
});

type DispatchedEvents =
  | TradeShownAllEvent
  | TradeHiddenAllEvent
  | TradeShownEvent
  | TradeHiddenEvent

const mapDispatchToProps = (dispatch: Dispatch<DispatchedEvents>) => bindActionCreators({
  tradeSelectedAll: tradeShownAll,
  tradeDeselectedAll: tradeHiddenAll,
  tradeShown,
  tradeHidden
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ConnectedTradeFilterPanel);
